/*
 * Night Sky Dashboard - Connected to Live Data Pipeline
 * Frontend: React + Three.js | Backend: FastAPI + NASA/CelesTrak/OWM
 */
import { useState, useEffect, useRef, useCallback } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { fetchDashboardData, fetchVisibility } from "./api";
import AnalyticsPanel from "./AnalyticsPanel";

const MODEL_PATH = "/models/earth/Earth_1_12756.glb";
const STARFIELD_PATH = "/textures/2k_stars_milky_way.jpg";

const ASTEROID_FILES = [
  "/models/asteroids/Asteroid 4 Vesta (B).stl",
  "/models/asteroids/Asteroid 216 Kleopatra.stl",
  "/models/asteroids/Asteroid 433 Eros.stl",
  "/models/asteroids/Asteroid 1620 Geographos.stl",
  "/models/asteroids/Asteroid 4179 Toutatis.stl",
  "/models/asteroids/Asteroid 4486 Mithra.stl",
  "/models/asteroids/Asteroid 6489 Golevka.stl",
  "/models/asteroids/Asteroid 8567 1996 HW1.stl",
  "/models/asteroids/Asteroid 25143 Itokawa.stl",
  "/models/asteroids/Asteroid 101955 Bennu.stl",
  "/models/asteroids/Asteroid 101955 Bennu.stl",
  "/models/asteroids/Asteroid 8567 1996 HW1.stl",
  "/models/asteroids/Asteroid 6489 Golevka.stl",
  "/models/asteroids/Asteroid 4486 Mithra.stl",
  "/models/asteroids/Asteroid 1620 Geographos.stl",
  "/models/asteroids/Asteroid 4 Vesta (B).stl",
  "/models/asteroids/Asteroid 216 Kleopatra.stl",
  "/models/asteroids/Asteroid 433 Eros.stl",
  "/models/asteroids/Asteroid 1620 Geographos.stl",
  "/models/asteroids/Asteroid 4179 Toutatis.stl",
  "/models/asteroids/Asteroid 4486 Mithra.stl",
  "/models/asteroids/Asteroid 6489 Golevka.stl",
  "/models/asteroids/Asteroid 8567 1996 HW1.stl",
  "/models/asteroids/Asteroid 25143 Itokawa.stl",
  "/models/asteroids/Asteroid 101955 Bennu.stl",
  "/models/asteroids/Asteroid 101955 Bennu.stl",
  "/models/asteroids/Asteroid 8567 1996 HW1.stl",
  "/models/asteroids/Asteroid 6489 Golevka.stl",
  "/models/asteroids/Asteroid 4486 Mithra.stl",
  "/models/asteroids/Asteroid 1620 Geographos.stl",
];

const SATELLITE_FILES = [
  "/models/satellites/Satellite for Scientific Applications (SAC-C).glb",
  "/models/satellites/Tracking and Data Relay Satellites (TDRS) (A).glb",
  "/models/satellites/Tracking and Data Relay Satellites (TDRS) (B).glb",
  "/models/satellites/Tracking and Data Relay Satellites (TDRS) (C).glb",
  "/models/satellites/Tracking and Data Relay Satellites (TDRS) (D).glb",
  "/models/satellites/Transiting Exoplanet Survey Satellite (TESS) (A).glb",
  "/models/satellites/Transiting Exoplanet Survey Satellite (TESS) (B).glb",
  "/models/satellites/Satellite for Scientific Applications (SAC-C).glb",
  "/models/satellites/Tracking and Data Relay Satellites (TDRS) (A).glb",
  "/models/satellites/Tracking and Data Relay Satellites (TDRS) (B).glb",
  "/models/satellites/Tracking and Data Relay Satellites (TDRS) (C).glb",
  "/models/satellites/Tracking and Data Relay Satellites (TDRS) (D).glb",
  "/models/satellites/Transiting Exoplanet Survey Satellite (TESS) (A).glb",
  "/models/satellites/Transiting Exoplanet Survey Satellite (TESS) (B).glb",
];

// ── FALLBACK DATA (used when API is unreachable) ───────────────
const FALLBACK_SHOWERS = [
  {
    name: "Quadrantids",
    peak_start: "Jan 3",
    peak_end: "Jan 4",
    zhr: 120,
    active_start: "Dec 28",
    active_end: "Jan 12",
  },
  {
    name: "Perseids",
    peak_start: "Aug 12",
    peak_end: "Aug 13",
    zhr: 100,
    active_start: "Jul 17",
    active_end: "Aug 24",
  },
  {
    name: "Geminids",
    peak_start: "Dec 13",
    peak_end: "Dec 14",
    zhr: 150,
    active_start: "Dec 4",
    active_end: "Dec 17",
  },
  {
    name: "Leonids",
    peak_start: "Nov 17",
    peak_end: "Nov 18",
    zhr: 15,
    active_start: "Nov 6",
    active_end: "Nov 30",
  },
  {
    name: "Eta Aquariids",
    peak_start: "May 5",
    peak_end: "May 6",
    zhr: 50,
    active_start: "Apr 19",
    active_end: "May 28",
  },
  {
    name: "Orionids",
    peak_start: "Oct 21",
    peak_end: "Oct 22",
    zhr: 20,
    active_start: "Oct 2",
    active_end: "Nov 7",
  },
];

const FALLBACK_LOCATIONS = [
  {lat:35.68,lng:139.65,city:"Tokyo",country:"Japan",bortle_class:8},
  {lat:39.91,lng:116.4,city:"Beijing",country:"China",bortle_class:8},
  {lat:31.23,lng:121.47,city:"Shanghai",country:"China",bortle_class:9},
  {lat:37.57,lng:126.98,city:"Seoul",country:"South Korea",bortle_class:8},
  {lat:34.69,lng:135.5,city:"Osaka",country:"Japan",bortle_class:8},
  {lat:22.32,lng:114.17,city:"Hong Kong",country:"China",bortle_class:9},
  {lat:25.03,lng:121.57,city:"Taipei",country:"Taiwan",bortle_class:8},
  {lat:22.54,lng:114.06,city:"Shenzhen",country:"China",bortle_class:8},
  {lat:23.13,lng:113.26,city:"Guangzhou",country:"China",bortle_class:8},
  {lat:30.57,lng:104.07,city:"Chengdu",country:"China",bortle_class:7},
  {lat:35.18,lng:129.08,city:"Busan",country:"South Korea",bortle_class:7},
  {lat:47.91,lng:106.91,city:"Ulaanbaatar",country:"Mongolia",bortle_class:4},
  {lat:39.02,lng:125.75,city:"Pyongyang",country:"North Korea",bortle_class:5},
  {lat:43.06,lng:141.35,city:"Sapporo",country:"Japan",bortle_class:6},
  {lat:33.59,lng:130.4,city:"Fukuoka",country:"Japan",bortle_class:7},
  {lat:30.59,lng:114.31,city:"Wuhan",country:"China",bortle_class:7},
  {lat:29.56,lng:106.55,city:"Chongqing",country:"China",bortle_class:7},
  {lat:32.06,lng:118.8,city:"Nanjing",country:"China",bortle_class:7},
  {lat:30.27,lng:120.15,city:"Hangzhou",country:"China",bortle_class:7},
  {lat:34.26,lng:108.94,city:"Xi'an",country:"China",bortle_class:6},
  {lat:13.76,lng:100.5,city:"Bangkok",country:"Thailand",bortle_class:8},
  {lat:-6.21,lng:106.85,city:"Jakarta",country:"Indonesia",bortle_class:8},
  {lat:14.6,lng:120.98,city:"Manila",country:"Philippines",bortle_class:8},
  {lat:1.35,lng:103.82,city:"Singapore",country:"Singapore",bortle_class:9},
  {lat:3.14,lng:101.69,city:"Kuala Lumpur",country:"Malaysia",bortle_class:8},
  {lat:10.82,lng:106.63,city:"Ho Chi Minh City",country:"Vietnam",bortle_class:7},
  {lat:21.03,lng:105.85,city:"Hanoi",country:"Vietnam",bortle_class:7},
  {lat:16.87,lng:96.2,city:"Yangon",country:"Myanmar",bortle_class:6},
  {lat:11.56,lng:104.92,city:"Phnom Penh",country:"Cambodia",bortle_class:6},
  {lat:-8.41,lng:115.19,city:"Bali",country:"Indonesia",bortle_class:4},
  {lat:17.97,lng:102.63,city:"Vientiane",country:"Laos",bortle_class:5},
  {lat:10.31,lng:123.89,city:"Cebu",country:"Philippines",bortle_class:6},
  {lat:-7.25,lng:112.75,city:"Surabaya",country:"Indonesia",bortle_class:7},
  {lat:18.79,lng:98.98,city:"Chiang Mai",country:"Thailand",bortle_class:5},
  {lat:16.05,lng:108.22,city:"Da Nang",country:"Vietnam",bortle_class:5},
  {lat:19.08,lng:72.88,city:"Mumbai",country:"India",bortle_class:9},
  {lat:28.61,lng:77.21,city:"New Delhi",country:"India",bortle_class:9},
  {lat:12.97,lng:77.59,city:"Bangalore",country:"India",bortle_class:7},
  {lat:22.57,lng:88.36,city:"Kolkata",country:"India",bortle_class:8},
  {lat:13.08,lng:80.27,city:"Chennai",country:"India",bortle_class:7},
  {lat:17.39,lng:78.49,city:"Hyderabad",country:"India",bortle_class:7},
  {lat:24.86,lng:67.01,city:"Karachi",country:"Pakistan",bortle_class:7},
  {lat:31.55,lng:74.35,city:"Lahore",country:"Pakistan",bortle_class:7},
  {lat:33.69,lng:73.04,city:"Islamabad",country:"Pakistan",bortle_class:5},
  {lat:23.81,lng:90.41,city:"Dhaka",country:"Bangladesh",bortle_class:8},
  {lat:-6.93,lng:79.84,city:"Colombo",country:"Sri Lanka",bortle_class:6},
  {lat:27.72,lng:85.32,city:"Kathmandu",country:"Nepal",bortle_class:5},
  {lat:43.24,lng:76.95,city:"Almaty",country:"Kazakhstan",bortle_class:5},
  {lat:41.3,lng:69.28,city:"Tashkent",country:"Uzbekistan",bortle_class:5},
  {lat:51.17,lng:71.43,city:"Astana",country:"Kazakhstan",bortle_class:4},
  {lat:42.87,lng:74.59,city:"Bishkek",country:"Kyrgyzstan",bortle_class:4},
  {lat:41.69,lng:44.8,city:"Tbilisi",country:"Georgia",bortle_class:5},
  {lat:25.2,lng:55.27,city:"Dubai",country:"UAE",bortle_class:7},
  {lat:21.39,lng:39.86,city:"Jeddah",country:"Saudi Arabia",bortle_class:5},
  {lat:24.71,lng:46.68,city:"Riyadh",country:"Saudi Arabia",bortle_class:6},
  {lat:31.95,lng:35.93,city:"Amman",country:"Jordan",bortle_class:5},
  {lat:35.69,lng:51.39,city:"Tehran",country:"Iran",bortle_class:7},
  {lat:41.01,lng:28.98,city:"Istanbul",country:"Turkey",bortle_class:7},
  {lat:25.29,lng:51.53,city:"Doha",country:"Qatar",bortle_class:6},
  {lat:29.38,lng:47.99,city:"Kuwait City",country:"Kuwait",bortle_class:6},
  {lat:23.61,lng:58.54,city:"Muscat",country:"Oman",bortle_class:4},
  {lat:33.31,lng:44.37,city:"Baghdad",country:"Iraq",bortle_class:6},
  {lat:33.89,lng:35.5,city:"Beirut",country:"Lebanon",bortle_class:7},
  {lat:32.07,lng:34.78,city:"Tel Aviv",country:"Israel",bortle_class:8},
  {lat:51.51,lng:-0.13,city:"London",country:"UK",bortle_class:8},
  {lat:48.86,lng:2.35,city:"Paris",country:"France",bortle_class:8},
  {lat:52.52,lng:13.41,city:"Berlin",country:"Germany",bortle_class:7},
  {lat:40.42,lng:-3.7,city:"Madrid",country:"Spain",bortle_class:7},
  {lat:41.9,lng:12.5,city:"Rome",country:"Italy",bortle_class:7},
  {lat:52.37,lng:4.9,city:"Amsterdam",country:"Netherlands",bortle_class:8},
  {lat:41.39,lng:2.17,city:"Barcelona",country:"Spain",bortle_class:7},
  {lat:48.14,lng:11.58,city:"Munich",country:"Germany",bortle_class:6},
  {lat:45.46,lng:9.19,city:"Milan",country:"Italy",bortle_class:7},
  {lat:38.72,lng:-9.14,city:"Lisbon",country:"Portugal",bortle_class:6},
  {lat:50.85,lng:4.35,city:"Brussels",country:"Belgium",bortle_class:7},
  {lat:48.21,lng:16.37,city:"Vienna",country:"Austria",bortle_class:6},
  {lat:47.38,lng:8.54,city:"Zurich",country:"Switzerland",bortle_class:6},
  {lat:53.35,lng:-6.26,city:"Dublin",country:"Ireland",bortle_class:6},
  {lat:55.95,lng:-3.19,city:"Edinburgh",country:"UK",bortle_class:6},
  {lat:53.55,lng:9.99,city:"Hamburg",country:"Germany",bortle_class:7},
  {lat:45.76,lng:4.84,city:"Lyon",country:"France",bortle_class:6},
  {lat:41.15,lng:-8.61,city:"Porto",country:"Portugal",bortle_class:5},
  {lat:43.3,lng:5.37,city:"Marseille",country:"France",bortle_class:6},
  {lat:46.2,lng:6.14,city:"Geneva",country:"Switzerland",bortle_class:5},
  {lat:59.33,lng:18.07,city:"Stockholm",country:"Sweden",bortle_class:5},
  {lat:59.91,lng:10.75,city:"Oslo",country:"Norway",bortle_class:5},
  {lat:60.17,lng:24.94,city:"Helsinki",country:"Finland",bortle_class:5},
  {lat:55.68,lng:12.57,city:"Copenhagen",country:"Denmark",bortle_class:6},
  {lat:64.15,lng:-21.94,city:"Reykjavik",country:"Iceland",bortle_class:3},
  {lat:69.65,lng:18.96,city:"Tromso",country:"Norway",bortle_class:3},
  {lat:59.44,lng:24.75,city:"Tallinn",country:"Estonia",bortle_class:5},
  {lat:56.95,lng:24.11,city:"Riga",country:"Latvia",bortle_class:5},
  {lat:54.69,lng:25.28,city:"Vilnius",country:"Lithuania",bortle_class:5},
  {lat:64.17,lng:-51.74,city:"Nuuk",country:"Greenland",bortle_class:2},
  {lat:55.76,lng:37.62,city:"Moscow",country:"Russia",bortle_class:7},
  {lat:59.93,lng:30.32,city:"St Petersburg",country:"Russia",bortle_class:6},
  {lat:52.23,lng:21.01,city:"Warsaw",country:"Poland",bortle_class:7},
  {lat:50.08,lng:14.44,city:"Prague",country:"Czech Republic",bortle_class:6},
  {lat:47.5,lng:19.04,city:"Budapest",country:"Hungary",bortle_class:6},
  {lat:44.43,lng:26.1,city:"Bucharest",country:"Romania",bortle_class:6},
  {lat:37.98,lng:23.73,city:"Athens",country:"Greece",bortle_class:7},
  {lat:42.7,lng:23.32,city:"Sofia",country:"Bulgaria",bortle_class:6},
  {lat:44.79,lng:20.47,city:"Belgrade",country:"Serbia",bortle_class:6},
  {lat:45.81,lng:15.98,city:"Zagreb",country:"Croatia",bortle_class:5},
  {lat:50.45,lng:30.52,city:"Kyiv",country:"Ukraine",bortle_class:6},
  {lat:53.9,lng:27.57,city:"Minsk",country:"Belarus",bortle_class:6},
  {lat:30.04,lng:31.24,city:"Cairo",country:"Egypt",bortle_class:6},
  {lat:33.57,lng:-7.59,city:"Casablanca",country:"Morocco",bortle_class:6},
  {lat:36.75,lng:3.04,city:"Algiers",country:"Algeria",bortle_class:6},
  {lat:36.81,lng:10.17,city:"Tunis",country:"Tunisia",bortle_class:6},
  {lat:31.63,lng:-8.0,city:"Marrakech",country:"Morocco",bortle_class:4},
  {lat:31.2,lng:29.92,city:"Alexandria",country:"Egypt",bortle_class:6},
  {lat:32.9,lng:13.18,city:"Tripoli",country:"Libya",bortle_class:5},
  {lat:25.69,lng:32.64,city:"Luxor",country:"Egypt",bortle_class:3},
  {lat:-1.29,lng:36.82,city:"Nairobi",country:"Kenya",bortle_class:4},
  {lat:6.52,lng:3.38,city:"Lagos",country:"Nigeria",bortle_class:7},
  {lat:-26.2,lng:28.05,city:"Johannesburg",country:"South Africa",bortle_class:6},
  {lat:-33.93,lng:18.42,city:"Cape Town",country:"South Africa",bortle_class:5},
  {lat:9.02,lng:38.75,city:"Addis Ababa",country:"Ethiopia",bortle_class:5},
  {lat:5.56,lng:-0.19,city:"Accra",country:"Ghana",bortle_class:6},
  {lat:-6.79,lng:39.28,city:"Dar es Salaam",country:"Tanzania",bortle_class:5},
  {lat:0.35,lng:32.58,city:"Kampala",country:"Uganda",bortle_class:5},
  {lat:-1.94,lng:30.06,city:"Kigali",country:"Rwanda",bortle_class:4},
  {lat:14.69,lng:-17.44,city:"Dakar",country:"Senegal",bortle_class:5},
  {lat:-8.84,lng:13.23,city:"Luanda",country:"Angola",bortle_class:5},
  {lat:-25.97,lng:32.57,city:"Maputo",country:"Mozambique",bortle_class:5},
  {lat:-17.83,lng:31.05,city:"Harare",country:"Zimbabwe",bortle_class:5},
  {lat:-22.56,lng:17.08,city:"Windhoek",country:"Namibia",bortle_class:3},
  {lat:-18.88,lng:47.51,city:"Antananarivo",country:"Madagascar",bortle_class:4},
  {lat:-4.44,lng:15.27,city:"Kinshasa",country:"DR Congo",bortle_class:5},
  {lat:9.06,lng:7.49,city:"Abuja",country:"Nigeria",bortle_class:5},
  {lat:-29.86,lng:31.02,city:"Durban",country:"South Africa",bortle_class:6},
  {lat:40.71,lng:-74.01,city:"New York",country:"USA",bortle_class:9},
  {lat:34.05,lng:-118.24,city:"Los Angeles",country:"USA",bortle_class:8},
  {lat:41.88,lng:-87.63,city:"Chicago",country:"USA",bortle_class:8},
  {lat:29.76,lng:-95.37,city:"Houston",country:"USA",bortle_class:7},
  {lat:33.45,lng:-112.07,city:"Phoenix",country:"USA",bortle_class:6},
  {lat:37.77,lng:-122.42,city:"San Francisco",country:"USA",bortle_class:7},
  {lat:47.61,lng:-122.33,city:"Seattle",country:"USA",bortle_class:6},
  {lat:39.74,lng:-104.98,city:"Denver",country:"USA",bortle_class:6},
  {lat:25.76,lng:-80.19,city:"Miami",country:"USA",bortle_class:7},
  {lat:42.36,lng:-71.06,city:"Boston",country:"USA",bortle_class:8},
  {lat:33.75,lng:-84.39,city:"Atlanta",country:"USA",bortle_class:7},
  {lat:32.78,lng:-96.8,city:"Dallas",country:"USA",bortle_class:7},
  {lat:38.91,lng:-77.04,city:"Washington DC",country:"USA",bortle_class:8},
  {lat:36.17,lng:-115.14,city:"Las Vegas",country:"USA",bortle_class:7},
  {lat:36.16,lng:-86.78,city:"Nashville",country:"USA",bortle_class:6},
  {lat:45.52,lng:-122.68,city:"Portland",country:"USA",bortle_class:6},
  {lat:44.98,lng:-93.27,city:"Minneapolis",country:"USA",bortle_class:6},
  {lat:29.95,lng:-90.07,city:"New Orleans",country:"USA",bortle_class:6},
  {lat:21.31,lng:-157.86,city:"Honolulu",country:"USA",bortle_class:4},
  {lat:61.22,lng:-149.9,city:"Anchorage",country:"USA",bortle_class:3},
  {lat:43.65,lng:-79.38,city:"Toronto",country:"Canada",bortle_class:7},
  {lat:49.28,lng:-123.12,city:"Vancouver",country:"Canada",bortle_class:6},
  {lat:45.5,lng:-73.57,city:"Montreal",country:"Canada",bortle_class:7},
  {lat:51.05,lng:-114.07,city:"Calgary",country:"Canada",bortle_class:5},
  {lat:45.42,lng:-75.7,city:"Ottawa",country:"Canada",bortle_class:5},
  {lat:19.43,lng:-99.13,city:"Mexico City",country:"Mexico",bortle_class:8},
  {lat:20.67,lng:-103.35,city:"Guadalajara",country:"Mexico",bortle_class:6},
  {lat:25.67,lng:-100.31,city:"Monterrey",country:"Mexico",bortle_class:6},
  {lat:21.16,lng:-86.85,city:"Cancun",country:"Mexico",bortle_class:4},
  {lat:23.11,lng:-82.37,city:"Havana",country:"Cuba",bortle_class:5},
  {lat:8.98,lng:-79.52,city:"Panama City",country:"Panama",bortle_class:6},
  {lat:9.93,lng:-84.08,city:"San Jose",country:"Costa Rica",bortle_class:5},
  {lat:14.63,lng:-90.51,city:"Guatemala City",country:"Guatemala",bortle_class:6},
  {lat:18.0,lng:-76.79,city:"Kingston",country:"Jamaica",bortle_class:5},
  {lat:18.49,lng:-69.93,city:"Santo Domingo",country:"Dominican Republic",bortle_class:6},
  {lat:18.47,lng:-66.11,city:"San Juan",country:"Puerto Rico",bortle_class:6},
  {lat:14.07,lng:-87.19,city:"Tegucigalpa",country:"Honduras",bortle_class:5},
  {lat:13.69,lng:-89.22,city:"San Salvador",country:"El Salvador",bortle_class:6},
  {lat:-23.55,lng:-46.63,city:"Sao Paulo",country:"Brazil",bortle_class:8},
  {lat:-22.91,lng:-43.17,city:"Rio de Janeiro",country:"Brazil",bortle_class:7},
  {lat:-34.6,lng:-58.38,city:"Buenos Aires",country:"Argentina",bortle_class:7},
  {lat:-12.05,lng:-77.04,city:"Lima",country:"Peru",bortle_class:7},
  {lat:4.71,lng:-74.07,city:"Bogota",country:"Colombia",bortle_class:6},
  {lat:-33.45,lng:-70.67,city:"Santiago",country:"Chile",bortle_class:6},
  {lat:10.48,lng:-66.9,city:"Caracas",country:"Venezuela",bortle_class:6},
  {lat:-0.18,lng:-78.47,city:"Quito",country:"Ecuador",bortle_class:5},
  {lat:-34.91,lng:-56.19,city:"Montevideo",country:"Uruguay",bortle_class:5},
  {lat:6.25,lng:-75.56,city:"Medellin",country:"Colombia",bortle_class:5},
  {lat:-15.79,lng:-47.88,city:"Brasilia",country:"Brazil",bortle_class:5},
  {lat:-13.52,lng:-71.97,city:"Cusco",country:"Peru",bortle_class:3},
  {lat:-16.5,lng:-68.15,city:"La Paz",country:"Bolivia",bortle_class:4},
  {lat:-25.26,lng:-57.58,city:"Asuncion",country:"Paraguay",bortle_class:5},
  {lat:-3.12,lng:-60.02,city:"Manaus",country:"Brazil",bortle_class:4},
  {lat:-12.97,lng:-38.51,city:"Salvador",country:"Brazil",bortle_class:6},
  {lat:10.39,lng:-75.51,city:"Cartagena",country:"Colombia",bortle_class:5},
  {lat:-54.8,lng:-68.3,city:"Ushuaia",country:"Argentina",bortle_class:2},
  {lat:-33.87,lng:151.21,city:"Sydney",country:"Australia",bortle_class:7},
  {lat:-37.81,lng:144.96,city:"Melbourne",country:"Australia",bortle_class:7},
  {lat:-36.85,lng:174.76,city:"Auckland",country:"New Zealand",bortle_class:5},
  {lat:-31.95,lng:115.86,city:"Perth",country:"Australia",bortle_class:5},
  {lat:-27.47,lng:153.03,city:"Brisbane",country:"Australia",bortle_class:6},
  {lat:-41.29,lng:174.78,city:"Wellington",country:"New Zealand",bortle_class:4},
  {lat:-34.93,lng:138.6,city:"Adelaide",country:"Australia",bortle_class:5},
  {lat:-45.03,lng:168.66,city:"Queenstown",country:"New Zealand",bortle_class:2},
  {lat:-17.77,lng:177.96,city:"Fiji",country:"Fiji",bortle_class:3},
  {lat:-43.53,lng:172.64,city:"Christchurch",country:"New Zealand",bortle_class:4},
  {lat:-12.46,lng:130.84,city:"Darwin",country:"Australia",bortle_class:4},
  {lat:-42.88,lng:147.33,city:"Hobart",country:"Australia",bortle_class:4}
].map(c => ({
  ...c,
  overall_score: Math.round(Math.max(15, Math.min(95, 78 - (c.bortle_class - 4) * 6 + (Math.sin(c.lat * 0.17) * 5)))),
  condition_main: "Clear",
  moon_phase: "—",
  moon_illumination: 0,
  best_time_start: "12:00 AM",
  best_time_end: "4:00 AM",
}));


// ── HELPERS ─────────────────────────────────────────────────────
// EARTH_LNG_OFFSET: calibrates marker longitude to match NASA Earth model orientation.
// The GLB model's prime meridian may face a different axis than the math assumes.
// Adjust this value (in degrees) if markers are still shifted east/west.
const EARTH_LNG_OFFSET = 270;

function latLngToVector3(lat, lng, r) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + EARTH_LNG_OFFSET) * (Math.PI / 180);
  return new THREE.Vector3(
    -r * Math.sin(phi) * Math.cos(theta),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta),
  );
}
function getScoreColor(s) {
  if (s >= 80) return "#00ff88";
  if (s >= 60) return "#88ff00";
  if (s >= 40) return "#ffaa00";
  return "#ff4444";
}
function getScoreLabel(s, labels) {
  if (s >= 80) return labels?.excellent || "Excellent";
  if (s >= 60) return labels?.good || "Good";
  if (s >= 40) return labels?.fair || "Fair";
  return labels?.poor || "Poor";
}
function getBortleLabel(b) {
  return (
    {
      1: "Excellent dark-sky",
      2: "Typical dark site",
      3: "Rural sky",
      4: "Rural/suburban",
      5: "Suburban sky",
      6: "Bright suburban",
      7: "Suburban/urban",
      8: "City sky",
      9: "Inner-city sky",
    }[b] || "Unknown"
  );
}
function normalizeModel(object, targetSize) {
  const box = new THREE.Box3().setFromObject(object);
  const size = new THREE.Vector3();
  box.getSize(size);
  const maxDim = Math.max(size.x, size.y, size.z);
  if (maxDim > 0) object.scale.multiplyScalar(targetSize / maxDim);
  box.setFromObject(object);
  const center = new THREE.Vector3();
  box.getCenter(center);
  object.position.sub(center);
}

// ── SHADERS ─────────────────────────────────────────────────────
const AV = `varying vec3 vN; void main() { vN = normalize(normalMatrix * normal); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;
const AF_IN = `varying vec3 vN; void main() { float i = pow(0.62 - dot(vN, vec3(0, 0, 1)), 4.0); gl_FragColor = vec4(0.3, 0.6, 1.0, 1.0) * i * 0.9; }`;
const AF_OUT = `varying vec3 vN; void main() { float i = pow(0.5 - dot(vN, vec3(0, 0, 1)), 3.0); gl_FragColor = vec4(0.2, 0.5, 1.0, 1.0) * i * 0.25; }`;

function getSunPosition(date = new Date()) {
  const J2000 = Date.UTC(2000, 0, 1, 12, 0, 0);
  const d = (date.getTime() - J2000) / 86400000;
  const L = (280.46 + 0.9856474 * d) % 360;
  const g = ((357.528 + 0.9856003 * d) % 360) * (Math.PI / 180);
  const lambda =
    (L + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * (Math.PI / 180);
  const epsilon = 23.439 * (Math.PI / 180);
  const sunDir = new THREE.Vector3(
    Math.cos(lambda),
    Math.sin(lambda) * Math.sin(epsilon),
    -Math.sin(lambda) * Math.cos(epsilon),
  );
  const utcH =
    date.getUTCHours() +
    date.getUTCMinutes() / 60 +
    date.getUTCSeconds() / 3600;
  sunDir.applyAxisAngle(new THREE.Vector3(0, 1, 0), -(utcH / 24) * Math.PI * 2);
  return sunDir;
}

function LoadingScreen({ progress, status }) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 100,
        background: "radial-gradient(ellipse at center, #060a14 0%, #000 70%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'JetBrains Mono', 'SF Mono', monospace",
        color: "#e0e8f0",
      }}
    >
      <div
        style={{
          position: "relative",
          width: 80,
          height: 80,
          marginBottom: 32,
        }}
      >
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            border: "2px solid rgba(0,170,255,0.1)",
            borderTopColor: "#00aaff",
            animation: "spin 1s linear infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            width: 12,
            height: 12,
            borderRadius: "50%",
            background: "#00aaff",
            boxShadow: "0 0 20px #00aaff",
          }}
        />
      </div>
      <div
        style={{
          fontSize: 11,
          letterSpacing: 4,
          textTransform: "uppercase",
          color: "#4466aa",
          marginBottom: 16,
        }}
      >
        Loading NASA Models
      </div>
      <div
        style={{
          width: 240,
          height: 3,
          background: "rgba(255,255,255,0.06)",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: "100%",
            background: "linear-gradient(90deg, #0066ff, #00aaff)",
            transition: "width 0.3s ease",
            boxShadow: "0 0 10px #00aaff",
          }}
        />
      </div>
      <div
        style={{
          fontSize: 10,
          color: "#334455",
          marginTop: 12,
          letterSpacing: 1,
        }}
      >
        {status}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
export default function NightSkyDashboard() {
  const mountRef = useRef(null);
  const sceneRef = useRef({});
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showVisibility, setShowVisibility] = useState(false);
  const [activeLayer, setActiveLayer] = useState({
    satellites: true,
    debris: true,
    asteroids: true,
  });
  const [loading, setLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [loadStatus, setLoadStatus] = useState("Initializing...");

  // ── Live Data State ──
  const [apiConnected, setApiConnected] = useState(false);
  const [meteorShowers, setMeteorShowers] = useState(FALLBACK_SHOWERS);
  const [visibilityLocations, setVisibilityLocations] =
    useState(FALLBACK_LOCATIONS);
  const [moonData, setMoonData] = useState(null);
  const [neoCount, setNeoCount] = useState(88);
  const [satCount, setSatCount] = useState(200);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);

  // ── Language ──
  const [lang, setLang] = useState("en");
  const t = {
    en: {
      title: "Night Sky Dashboard",
      subtitle: "METEOR SHOWERS · SATELLITES · SPACE DEBRIS · NEOs",
      liveData: "LIVE DATA",
      hideVis: "HIDE VISIBILITY",
      showVis: "SHOW VISIBILITY",
      satellites: "SATELLITES",
      spaceDebris: "SPACE DEBRIS",
      neoAsteroids: "NEO ASTEROIDS",
      analytics: "ANALYTICS",
      meteorShowers: "METEOR SHOWERS",
      live: "LIVE",
      peak: "Peak:",
      visReport: "VISIBILITY REPORT",
      bestTime: "Best Time",
      weather: "Weather",
      moonPhase: "Moon Phase",
      lightPollution: "Light Pollution",
      scoreBreakdown: "SCORE BREAKDOWN",
      weatherPct: "Weather",
      moonPct: "Moon",
      lightPct: "Light Pollution",
      showerPct: "Shower Activity",
      dragRotate: "DRAG TO ROTATE · SCROLL TO ZOOM",
      clickMarkers: "CLICK MARKERS FOR DETAILS",
      earthCredits: "Earth, Asteroids & Satellites: NASA 3D Resources",
      dataCredits: "Data: NASA NeoWs · CelesTrak · OpenWeatherMap · IMO",
      excellent: "Excellent",
      good: "Good",
      fair: "Fair",
      poor: "Poor",
      loading: "Initializing Night Sky…",
      newMoon: "New Moon",
      waxCrescent: "Waxing Crescent",
      firstQuarter: "First Quarter",
      waxGibbous: "Waxing Gibbous",
      fullMoon: "Full Moon",
      wanGibbous: "Waning Gibbous",
      lastQuarter: "Last Quarter",
      wanCrescent: "Waning Crescent",
      clear: "Clear",
      fewClouds: "Few Clouds",
      scattered: "Scattered Clouds",
      broken: "Broken Clouds",
      overcast: "Overcast",
      rain: "Rain",
      snow: "Snow",
      thunderstorm: "Thunderstorm",
      unknown: "Unknown",
      updated: "Updated",
      visScore: "Visibility Score",
      offline: "Offline",
    },
    ar: {
      title: "سماء الليل",
      subtitle: "شهب · أقمار صناعية · حطام فضائي · كويكبات",
      liveData: "مباشر",
      hideVis: "إخفاء الرصد",
      showVis: "عرض الرصد",
      satellites: "أقمار صناعية",
      spaceDebris: "حطام فضائي",
      neoAsteroids: "كويكبات قريبة",
      analytics: "التحليلات",
      meteorShowers: "زخات شهب",
      live: "مباشر",
      peak: "الذروة:",
      visReport: "تقرير الرصد",
      bestTime: "أفضل وقت",
      weather: "الطقس",
      moonPhase: "طور القمر",
      lightPollution: "تلوث ضوئي",
      scoreBreakdown: "تفاصيل التقييم",
      weatherPct: "الطقس",
      moonPct: "القمر",
      lightPct: "تلوث ضوئي",
      showerPct: "نشاط الشهب",
      dragRotate: "اسحب للتدوير · مرر للتكبير",
      clickMarkers: "اضغط على النقاط للتفاصيل",
      earthCredits: "الأرض والكويكبات والأقمار: NASA 3D",
      dataCredits: "البيانات: NASA · CelesTrak · OpenWeather · IMO",
      excellent: "ممتاز",
      good: "جيد",
      fair: "مقبول",
      poor: "ضعيف",
      loading: "جاري التحميل...",
      newMoon: "محاق",
      waxCrescent: "هلال متزايد",
      firstQuarter: "تربيع أول",
      waxGibbous: "أحدب متزايد",
      fullMoon: "بدر",
      wanGibbous: "أحدب متناقص",
      lastQuarter: "تربيع أخير",
      wanCrescent: "هلال متناقص",
      clear: "صافي",
      fewClouds: "غيوم خفيفة",
      scattered: "غيوم متفرقة",
      broken: "غيوم كثيفة",
      overcast: "غائم",
      rain: "مطر",
      snow: "ثلج",
      thunderstorm: "عواصف رعدية",
      unknown: "غير معروف",
      updated: "تحديث",
      visScore: "تقييم الرصد",
      offline: "غير متصل",
    },
  }[lang];

  const isRTL = lang === "ar";

  // ── Responsive ──
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── Fetch Live Data ──
  useEffect(() => {
    let timer;
    async function load() {
      const data = await fetchDashboardData();
      if (data) {
        setApiConnected(true);
        setLastUpdate(new Date().toLocaleTimeString());
        if (data.meteor_showers?.showers?.length)
          setMeteorShowers(data.meteor_showers.showers);
        if (data.visibility?.locations?.length)
          setVisibilityLocations(data.visibility.locations);
        if (data.moon?.today) setMoonData(data.moon.today);
        if (data.neo) setNeoCount(data.neo.total || 88);
        if (data.satellites) setSatCount(data.satellites.total || 200);
      }
    }
    load();
    timer = setInterval(
      async () => {
        try {
          const vis = await fetchVisibility();
          if (vis?.locations?.length) {
            setVisibilityLocations(vis.locations);
            setLastUpdate(new Date().toLocaleTimeString());
          }
        } catch (_) {}
      },
      5 * 60 * 1000,
    );
    return () => clearInterval(timer);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initScene = useCallback(async () => {
    const container = mountRef.current;
    if (!container) return;
    const w = container.clientWidth,
      h = container.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 2000);
    camera.position.set(0, 0, 4.5);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 2.0;
    container.appendChild(renderer.domElement);

    // Starfield
    setLoadStatus("Loading starfield...");
    setLoadProgress(3);
    const texLoader = new THREE.TextureLoader();
    try {
      const starsTex = await new Promise((res, rej) =>
        texLoader.load(STARFIELD_PATH, res, undefined, rej),
      );
      scene.add(
        new THREE.Mesh(
          new THREE.SphereGeometry(500, 32, 32),
          new THREE.MeshBasicMaterial({ map: starsTex, side: THREE.BackSide }),
        ),
      );
    } catch (e) {
      const sg = new THREE.BufferGeometry();
      const sp = [];
      for (let i = 0; i < 8000; i++) {
        const r = 80 + Math.random() * 200,
          t = Math.random() * Math.PI * 2,
          p = Math.acos(2 * Math.random() - 1);
        sp.push(
          r * Math.sin(p) * Math.cos(t),
          r * Math.cos(p),
          r * Math.sin(p) * Math.sin(t),
        );
      }
      sg.setAttribute("position", new THREE.Float32BufferAttribute(sp, 3));
      scene.add(
        new THREE.Points(
          sg,
          new THREE.PointsMaterial({
            color: 0xffffff,
            size: 0.15,
            sizeAttenuation: true,
          }),
        ),
      );
    }

    // Lighting
    scene.add(new THREE.HemisphereLight(0x4466aa, 0x223344, 1.2));
    const sunLight = new THREE.DirectionalLight(0xfff5e6, 4.0);
    scene.add(sunLight);
    const fillLight = new THREE.DirectionalLight(0x223355, 1.0);
    scene.add(fillLight);

    const R = 1.5;
    const earthGroup = new THREE.Group();

    // NASA Earth model
    setLoadStatus("Loading NASA Earth model...");
    setLoadProgress(5);
    try {
      const gltf = await new Promise((resolve, reject) => {
        new GLTFLoader().load(
          MODEL_PATH,
          resolve,
          (e) => {
            if (e.total > 0) {
              setLoadProgress(Math.floor((e.loaded / e.total) * 25) + 5);
              setLoadStatus(
                `Loading Earth... ${Math.floor(e.loaded / 1024)}KB`,
              );
            }
          },
          reject,
        );
      });
      const model = gltf.scene;
      const box = new THREE.Box3().setFromObject(model);
      const size = new THREE.Vector3();
      box.getSize(size);
      model.scale.setScalar((R * 2) / Math.max(size.x, size.y, size.z));
      box.setFromObject(model);
      const center = new THREE.Vector3();
      box.getCenter(center);
      model.position.sub(center);
      model.traverse((child) => {
        if (child.isMesh && child.material) {
          child.material.metalness = 0.1;
          child.material.roughness = 0.8;
          child.material.emissive = new THREE.Color(0x223344);
          child.material.emissiveIntensity = 0.25;
          child.material.needsUpdate = true;
        }
      });
      earthGroup.add(model);
    } catch (err) {
      console.warn("Earth fallback:", err);
      const c = document.createElement("canvas");
      c.width = 1024;
      c.height = 512;
      const ctx = c.getContext("2d");
      const grad = ctx.createLinearGradient(0, 0, 0, 512);
      grad.addColorStop(0, "#0a1e3d");
      grad.addColorStop(0.5, "#0c3060");
      grad.addColorStop(1, "#0a1e3d");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 1024, 512);
      ctx.fillStyle = "#2a5a22";
      [
        [220, 160, 80, 60],
        [290, 310, 40, 70],
        [500, 150, 40, 30],
        [520, 270, 50, 70],
        [650, 160, 100, 60],
        [780, 330, 40, 30],
      ].forEach(([x, y, rx, ry]) => {
        ctx.beginPath();
        ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
        ctx.fill();
      });
      earthGroup.add(
        new THREE.Mesh(
          new THREE.SphereGeometry(R, 64, 64),
          new THREE.MeshPhongMaterial({
            map: new THREE.CanvasTexture(c),
            specular: 0x222244,
            shininess: 25,
          }),
        ),
      );
    }
    scene.add(earthGroup);

    // Atmosphere
    scene.add(
      new THREE.Mesh(
        new THREE.SphereGeometry(R * 1.025, 64, 64),
        new THREE.ShaderMaterial({
          vertexShader: AV,
          fragmentShader: AF_IN,
          blending: THREE.AdditiveBlending,
          side: THREE.BackSide,
          transparent: true,
          depthWrite: false,
        }),
      ),
    );
    scene.add(
      new THREE.Mesh(
        new THREE.SphereGeometry(R * 1.12, 64, 64),
        new THREE.ShaderMaterial({
          vertexShader: AV,
          fragmentShader: AF_OUT,
          blending: THREE.AdditiveBlending,
          side: THREE.BackSide,
          transparent: true,
          depthWrite: false,
        }),
      ),
    );

    // NASA Asteroids (STL)
    setLoadStatus("Loading NASA asteroid models...");
    setLoadProgress(35);
    const stlLoader = new STLLoader();
    const asteroidGeometries = [];
    for (let i = 0; i < ASTEROID_FILES.length; i++) {
      try {
        const geometry = await new Promise((resolve, reject) =>
          stlLoader.load(ASTEROID_FILES[i], resolve, undefined, reject),
        );
        geometry.computeVertexNormals();
        asteroidGeometries.push(geometry);
        setLoadProgress(
          35 + Math.floor(((i + 1) / ASTEROID_FILES.length) * 15),
        );
        setLoadStatus(`Loaded asteroid ${i + 1}/${ASTEROID_FILES.length}`);
      } catch (err) {
        console.warn(`Asteroid load failed: ${ASTEROID_FILES[i]}`);
      }
    }

    const neoMeshes = [];
    const neoGroup = new THREE.Group();
    for (let i = 0; i < 30; i++) {
      let mesh;
      if (asteroidGeometries.length > 0) {
        const geo = asteroidGeometries[i % asteroidGeometries.length].clone();
        mesh = new THREE.Mesh(
          geo,
          new THREE.MeshStandardMaterial({
            color: new THREE.Color().setHSL(
              0.06 + Math.random() * 0.08,
              0.2 + Math.random() * 0.3,
              0.25 + Math.random() * 0.15,
            ),
            roughness: 0.9,
            metalness: 0.1,
            flatShading: true,
          }),
        );
        normalizeModel(mesh, 0.03 + Math.random() * 0.06);
        mesh.rotation.set(
          Math.random() * Math.PI * 2,
          Math.random() * Math.PI * 2,
          Math.random() * Math.PI * 2,
        );
      } else {
        const sz = 0.015 + Math.random() * 0.04;
        const geo = new THREE.IcosahedronGeometry(
          sz,
          Math.random() > 0.5 ? 1 : 0,
        );
        const pa = geo.attributes.position;
        for (let j = 0; j < pa.count; j++) {
          const n = 0.7 + Math.random() * 0.6;
          pa.setXYZ(j, pa.getX(j) * n, pa.getY(j) * n, pa.getZ(j) * n);
        }
        geo.computeVertexNormals();
        mesh = new THREE.Mesh(
          geo,
          new THREE.MeshPhongMaterial({
            color: new THREE.Color().setHSL(0.06, 0.3, 0.25),
            flatShading: true,
            shininess: 5,
          }),
        );
      }
      mesh.userData = {
        orbitR: R * (2 + Math.random() * 2.5),
        angle: Math.random() * Math.PI * 2,
        inc: (Math.random() - 0.5) * 1.2,
        speed: 0.00005 + Math.random() * 0.0003,
        ecc: 0.8 + Math.random() * 0.4,
        spinX: (Math.random() - 0.5) * 0.02,
        spinY: (Math.random() - 0.5) * 0.02,
        spinZ: (Math.random() - 0.5) * 0.015,
      };
      neoMeshes.push(mesh);
      neoGroup.add(mesh);
    }
    scene.add(neoGroup);

    // NASA Satellites (GLB)
    setLoadStatus("Loading NASA satellite models...");
    setLoadProgress(55);
    const gltfLoader = new GLTFLoader();
    const satelliteTemplates = [];
    for (let i = 0; i < SATELLITE_FILES.length; i++) {
      try {
        const gltf = await new Promise((resolve, reject) =>
          gltfLoader.load(SATELLITE_FILES[i], resolve, undefined, reject),
        );
        satelliteTemplates.push(gltf.scene);
        setLoadProgress(
          55 + Math.floor(((i + 1) / SATELLITE_FILES.length) * 20),
        );
        setLoadStatus(`Loaded satellite ${i + 1}/${SATELLITE_FILES.length}`);
      } catch (err) {
        console.warn(`Satellite load failed: ${SATELLITE_FILES[i]}`);
      }
    }

    const realSatMeshes = [];
    const realSatGroup = new THREE.Group();
    for (let i = 0; i < 20; i++) {
      if (!satelliteTemplates.length) break;
      const sat = satelliteTemplates[i % satelliteTemplates.length].clone();
      normalizeModel(sat, 0.04 + Math.random() * 0.03);
      sat.traverse((child) => {
        if (child.isMesh && child.material) {
          child.material = child.material.clone();
          child.material.emissive = new THREE.Color(0x224466);
          child.material.emissiveIntensity = 0.3;
          child.material.needsUpdate = true;
        }
      });
      sat.userData = {
        orbitR: R * (1.15 + Math.random() * 0.4),
        angle: Math.random() * Math.PI * 2,
        inc: (Math.random() - 0.5) * Math.PI * 0.6,
        speed: 0.0004 + Math.random() * 0.0008,
        tilt: Math.random() * Math.PI,
        spinY: (Math.random() - 0.5) * 0.005,
      };
      realSatMeshes.push(sat);
      realSatGroup.add(sat);
    }
    scene.add(realSatGroup);

    // Satellite particles (200)
    setLoadStatus("Building orbital objects...");
    setLoadProgress(80);
    const satPCount = 200;
    const satGeo = new THREE.BufferGeometry();
    const satPos = new Float32Array(satPCount * 3);
    const satCol = new Float32Array(satPCount * 3);
    const satData = [];
    for (let i = 0; i < satPCount; i++) {
      satData.push({
        orbitR: R * (1.12 + Math.random() * 0.5),
        angle: Math.random() * Math.PI * 2,
        inc: (Math.random() - 0.5) * Math.PI * 0.8,
        speed: 0.0003 + Math.random() * 0.001,
        tilt: Math.random() * Math.PI,
      });
      const c = new THREE.Color().setHSL(
        0.55 + Math.random() * 0.1,
        0.4 + Math.random() * 0.4,
        0.7 + Math.random() * 0.3,
      );
      satCol[i * 3] = c.r;
      satCol[i * 3 + 1] = c.g;
      satCol[i * 3 + 2] = c.b;
    }
    satGeo.setAttribute("position", new THREE.BufferAttribute(satPos, 3));
    satGeo.setAttribute("color", new THREE.BufferAttribute(satCol, 3));
    const sats = new THREE.Points(
      satGeo,
      new THREE.PointsMaterial({
        size: 0.025,
        vertexColors: true,
        transparent: true,
        opacity: 0.9,
        sizeAttenuation: true,
      }),
    );
    scene.add(sats);

    // Debris (400)
    const debCount = 400;
    const debGeo = new THREE.BufferGeometry();
    const debPos = new Float32Array(debCount * 3);
    const debCol = new Float32Array(debCount * 3);
    const debData = [];
    for (let i = 0; i < debCount; i++) {
      debData.push({
        orbitR: R * (1.06 + Math.random() * 0.8),
        angle: Math.random() * Math.PI * 2,
        inc: (Math.random() - 0.5) * Math.PI,
        speed: 0.0001 + Math.random() * 0.0005,
        tilt: Math.random() * Math.PI,
      });
      const c = new THREE.Color().setHSL(
        0.05 + Math.random() * 0.05,
        0.5,
        0.35 + Math.random() * 0.2,
      );
      debCol[i * 3] = c.r;
      debCol[i * 3 + 1] = c.g;
      debCol[i * 3 + 2] = c.b;
    }
    debGeo.setAttribute("position", new THREE.BufferAttribute(debPos, 3));
    debGeo.setAttribute("color", new THREE.BufferAttribute(debCol, 3));
    const debris = new THREE.Points(
      debGeo,
      new THREE.PointsMaterial({
        size: 0.012,
        vertexColors: true,
        transparent: true,
        opacity: 0.5,
        sizeAttenuation: true,
      }),
    );
    scene.add(debris);

    // ══════════════════════════════════════════════════════════════
    //  ENHANCED SHOOTING STAR / METEOR SYSTEM
    // ══════════════════════════════════════════════════════════════
    const shootingStars = [];
    const ssGroup = new THREE.Group();
    scene.add(ssGroup);

    // Meteor color palettes — based on real mineral compositions
    const METEOR_PALETTES = [
      { head: [1.0, 1.0, 1.0], trail: [0.5, 0.6, 1.0] },   // White → blue (ice/iron)
      { head: [1.0, 0.95, 0.8], trail: [0.7, 0.45, 0.15] }, // Warm white → orange (sodium)
      { head: [0.6, 0.9, 1.0], trail: [0.3, 0.5, 0.8] },    // Cyan → deep blue (magnesium)
      { head: [1.0, 1.0, 0.9], trail: [0.4, 0.8, 0.3] },    // White → green (nickel)
      { head: [1.0, 0.85, 0.6], trail: [0.9, 0.4, 0.1] },   // Gold → red-orange (fireball)
    ];

    const spawnSS = () => {
      const isBright = Math.random() < 0.18;
      const trail = isBright ? (28 + Math.floor(Math.random() * 14)) : (14 + Math.floor(Math.random() * 8));
      const palette = METEOR_PALETTES[Math.floor(Math.random() * METEOR_PALETTES.length)];

      const pos = new Float32Array(trail * 3);
      const colors = new Float32Array(trail * 3);

      // Spawn across the sky dome (hemisphere above globe)
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * 0.5 + 0.1;
      const dist = 4 + Math.random() * 5;
      const start = new THREE.Vector3(
        dist * Math.sin(phi) * Math.cos(theta),
        1.5 + Math.random() * 4,
        dist * Math.sin(phi) * Math.sin(theta),
      );

      // Velocity — mostly downward/inward, varied angles
      const spd = isBright ? (0.1 + Math.random() * 0.06) : (0.05 + Math.random() * 0.07);
      const vel = new THREE.Vector3(
        (Math.random() - 0.5) * 0.14,
        -spd - Math.random() * 0.03,
        (Math.random() - 0.5) * 0.14,
      );

      // Initialize all trail points at start + build vertex colors (head bright, tail fades)
      for (let i = 0; i < trail; i++) {
        pos[i * 3] = start.x;
        pos[i * 3 + 1] = start.y;
        pos[i * 3 + 2] = start.z;
        const fade = Math.pow(1.0 - i / trail, 1.5); // non-linear fade — head stays bright longer
        const c = i < 3 ? palette.head : palette.trail;
        colors[i * 3]     = c[0] * fade;
        colors[i * 3 + 1] = c[1] * fade;
        colors[i * 3 + 2] = c[2] * fade;
      }

      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
      geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
      const pts = new THREE.Points(geo, new THREE.PointsMaterial({
        size: isBright ? 0.035 : 0.018,
        vertexColors: true,
        transparent: true,
        opacity: 1.0,
        blending: THREE.AdditiveBlending,
        sizeAttenuation: true,
        depthWrite: false,
      }));
      ssGroup.add(pts);

      // Head glow — extra bright sphere for fireballs
      let headGlow = null;
      if (isBright) {
        headGlow = new THREE.Mesh(
          new THREE.SphereGeometry(0.025, 6, 6),
          new THREE.MeshBasicMaterial({
            color: new THREE.Color(palette.head[0], palette.head[1], palette.head[2]),
            transparent: true, opacity: 0.95,
            blending: THREE.AdditiveBlending, depthWrite: false,
          })
        );
        headGlow.position.copy(start);
        ssGroup.add(headGlow);
      }

      shootingStars.push({
        pts, pos, colors, start: start.clone(), vel,
        life: 0,
        max: isBright ? (75 + Math.random() * 45) : (45 + Math.random() * 30),
        trail, isBright, headGlow, palette,
        gravity: 0.00015 + Math.random() * 0.0002,
      });
    };

    // Fragment burst when a fireball dies
    const spawnFragments = (position, palette) => {
      const count = 5 + Math.floor(Math.random() * 4);
      const fragGeo = new THREE.BufferGeometry();
      const fragPos = new Float32Array(count * 3);
      const fragCol = new Float32Array(count * 3);
      for (let i = 0; i < count; i++) {
        fragPos[i * 3] = position.x;
        fragPos[i * 3 + 1] = position.y;
        fragPos[i * 3 + 2] = position.z;
        fragCol[i * 3] = palette.trail[0];
        fragCol[i * 3 + 1] = palette.trail[1];
        fragCol[i * 3 + 2] = palette.trail[2];
      }
      fragGeo.setAttribute("position", new THREE.BufferAttribute(fragPos, 3));
      fragGeo.setAttribute("color", new THREE.BufferAttribute(fragCol, 3));
      const fragPts = new THREE.Points(fragGeo, new THREE.PointsMaterial({
        size: 0.015, vertexColors: true, transparent: true, opacity: 1.0,
        blending: THREE.AdditiveBlending, sizeAttenuation: true, depthWrite: false,
      }));
      ssGroup.add(fragPts);
      const vels = [];
      for (let i = 0; i < count; i++) {
        vels.push(new THREE.Vector3(
          (Math.random() - 0.5) * 0.04,
          (Math.random() - 0.5) * 0.04,
          (Math.random() - 0.5) * 0.04,
        ));
      }
      return { fragPts, fragPos, vels, life: 0, max: 25 + Math.random() * 15 };
    };
    const fragments = [];

    // Visibility markers
    setLoadStatus("Placing markers...");
    setLoadProgress(92);
    const markers = [];
    const mGroup = new THREE.Group();
    // Shared geometries for 200 markers (saves memory)
    const glowGeo = new THREE.SphereGeometry(0.025, 8, 8);
    const dotGeo = new THREE.SphereGeometry(0.012, 6, 6);
    const beamGeo = new THREE.CylinderGeometry(0.0012, 0.0012, 0.1, 4);
    FALLBACK_LOCATIONS.forEach((loc) => {
      const score = loc.overall_score ?? 50;
      const p = latLngToVector3(loc.lat, loc.lng, R * 1.012);
      const col = new THREE.Color(getScoreColor(score));
      const glow = new THREE.Mesh(
        glowGeo,
        new THREE.MeshBasicMaterial({
          color: col,
          transparent: true,
          opacity: 0,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        }),
      );
      glow.position.copy(p);
      mGroup.add(glow);
      const dot = new THREE.Mesh(
        dotGeo,
        new THREE.MeshBasicMaterial({
          color: col,
          transparent: true,
          opacity: 0,
        }),
      );
      dot.position.copy(p);
      dot.userData = loc;
      mGroup.add(dot);
      const bp = latLngToVector3(loc.lat, loc.lng, R * 1.065);
      const beam = new THREE.Mesh(
        beamGeo,
        new THREE.MeshBasicMaterial({
          color: col,
          transparent: true,
          opacity: 0,
          blending: THREE.AdditiveBlending,
        }),
      );
      beam.position.copy(bp);
      beam.lookAt(0, 0, 0);
      beam.rotateX(Math.PI / 2);
      mGroup.add(beam);
      markers.push({ dot, glow, beam, loc });
    });
    scene.add(mGroup);
    sceneRef.current.markers = markers;

    // Controls
    let drag = false,
      prev = { x: 0, y: 0 },
      tgt = { x: 0.15, y: 0 },
      auto = true,
      zoom = 4.5;
    const onD = (x, y) => {
      drag = true;
      auto = false;
      prev = { x, y };
    };
    const onM = (x, y) => {
      if (!drag) return;
      tgt.x = Math.max(
        -Math.PI / 2,
        Math.min(Math.PI / 2, tgt.x + (y - prev.y) * 0.003),
      );
      tgt.y += (x - prev.x) * 0.003;
      prev = { x, y };
    };
    const onU = () => {
      drag = false;
      setTimeout(() => {
        auto = true;
      }, 3000);
    };
    const el = renderer.domElement;
    el.addEventListener("mousedown", (e) => onD(e.clientX, e.clientY));
    el.addEventListener("mousemove", (e) => onM(e.clientX, e.clientY));
    el.addEventListener("mouseup", onU);
    el.addEventListener("mouseleave", onU);
    el.addEventListener("wheel", (e) => {
      zoom = Math.max(2.2, Math.min(10, zoom + e.deltaY * 0.003));
    });
    // ── TOUCH: single-finger rotate + two-finger pinch zoom ──
    let lastPinchDist = 0;
    el.addEventListener("touchstart", (e) => {
      e.preventDefault();
      if (e.touches.length === 1) {
        drag = true; auto = false;
        prev = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      } else if (e.touches.length === 2) {
        drag = false;
        lastPinchDist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
      }
    }, { passive: false });
    el.addEventListener("touchmove", (e) => {
      e.preventDefault();
      if (e.touches.length === 1 && drag) {
        tgt.x += (e.touches[0].clientY - prev.y) * 0.003;
        tgt.y += (e.touches[0].clientX - prev.x) * 0.003;
        tgt.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, tgt.x));
        prev = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      } else if (e.touches.length === 2) {
        const dist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        const delta = lastPinchDist - dist;
        zoom = Math.max(2.2, Math.min(10, zoom + delta * 0.01));
        lastPinchDist = dist;
      }
    }, { passive: false });
    el.addEventListener("touchend", () => {
      drag = false;
      lastPinchDist = 0;
      setTimeout(() => auto = true, 3000);
    });
    const ray = new THREE.Raycaster();
    const mpos = new THREE.Vector2();
    el.addEventListener("click", (e) => {
      const rect = el.getBoundingClientRect();
      mpos.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mpos.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      ray.setFromCamera(mpos, camera);
      const hits = ray.intersectObjects(markers.map((m) => m.dot));
      const camDir = camera.position.clone().normalize();
      for (const hit of hits) {
        const markerDir = hit.object.position.clone().normalize();
        if (markerDir.dot(camDir) > 0.05) {
          setSelectedLocation(hit.object.userData);
          break;
        }
      }
    });

    // Animation
    let t = 0,
      ssT = 0;
    const animate = () => {
      const fid = requestAnimationFrame(animate);
      sceneRef.current.frameId = fid;
      t += 0.01;
      if (auto) tgt.y += 0.0008;
      camera.position.set(
        zoom * Math.sin(tgt.y) * Math.cos(tgt.x),
        zoom * Math.sin(tgt.x),
        zoom * Math.cos(tgt.y) * Math.cos(tgt.x),
      );
      camera.lookAt(0, 0, 0);
      const sunDir = getSunPosition();
      sunLight.position.copy(sunDir.clone().multiplyScalar(10));
      fillLight.position.copy(sunDir.clone().negate().multiplyScalar(5));
      const showS = !!sceneRef.current.layers?.satellites;
      if (showS) {
        const p = sats.geometry.attributes.position.array;
        satData.forEach((d, i) => {
          d.angle += d.speed;
          const cA = Math.cos(d.angle),
            sA = Math.sin(d.angle),
            cI = Math.cos(d.inc),
            sI = Math.sin(d.inc),
            cT = Math.cos(d.tilt),
            sT = Math.sin(d.tilt);
          const x = d.orbitR * cA * cI;
          let y = d.orbitR * sI * sA,
            z = d.orbitR * sA * cI;
          p[i * 3] = x;
          p[i * 3 + 1] = y * cT - z * sT;
          p[i * 3 + 2] = y * sT + z * cT;
        });
        sats.geometry.attributes.position.needsUpdate = true;
      }
      sats.visible = showS;
      realSatGroup.visible = showS;
      if (showS)
        realSatMeshes.forEach((sat) => {
          const u = sat.userData;
          u.angle += u.speed;
          const cA = Math.cos(u.angle),
            sA = Math.sin(u.angle),
            cI = Math.cos(u.inc),
            sI = Math.sin(u.inc),
            cT = Math.cos(u.tilt),
            sT = Math.sin(u.tilt);
          sat.position.set(
            u.orbitR * cA * cI,
            u.orbitR * sI * sA * cT - u.orbitR * sA * cI * sT,
            u.orbitR * sI * sA * sT + u.orbitR * sA * cI * cT,
          );
          sat.rotation.y += u.spinY;
        });
      const showD = !!sceneRef.current.layers?.debris;
      if (showD) {
        const p = debris.geometry.attributes.position.array;
        debData.forEach((d, i) => {
          d.angle += d.speed;
          p[i * 3] = d.orbitR * Math.cos(d.angle) * Math.cos(d.inc);
          p[i * 3 + 1] =
            d.orbitR * Math.sin(d.inc) * Math.sin(d.angle + d.tilt);
          p[i * 3 + 2] = d.orbitR * Math.sin(d.angle) * Math.cos(d.inc);
        });
        debris.geometry.attributes.position.needsUpdate = true;
      }
      debris.visible = showD;
      const showA = !!sceneRef.current.layers?.asteroids;
      neoMeshes.forEach((m) => {
        m.visible = showA;
        if (showA) {
          const u = m.userData;
          u.angle += u.speed;
          const r2 =
            (u.orbitR * (1 + u.ecc * Math.cos(u.angle * 0.5))) / (1 + u.ecc);
          m.position.set(
            r2 * Math.cos(u.angle) * Math.cos(u.inc),
            r2 * Math.sin(u.inc) * Math.sin(u.angle * 0.7),
            r2 * Math.sin(u.angle) * Math.cos(u.inc),
          );
          m.rotation.x += u.spinX;
          m.rotation.y += u.spinY;
          m.rotation.z += u.spinZ;
        }
      });
      const vis = sceneRef.current.showVisibility;
      const camN = camera.position.clone().normalize();
      const visLocs = sceneRef.current.visLocs;
      // Update marker colors when live data arrives
      if (visLocs && visLocs !== sceneRef.current._lastVisLocs) {
        sceneRef.current._lastVisLocs = visLocs;
        const colorMap = {};
        visLocs.forEach(v => { if (v.overall_score !== undefined) colorMap[v.city] = getScoreColor(v.overall_score); });
        markers.forEach(({ dot, glow, beam, loc }) => {
          const hex = colorMap[loc.city];
          if (hex) {
            const c = new THREE.Color(hex);
            dot.material.color.copy(c);
            glow.material.color.copy(c);
            beam.material.color.copy(c);
          }
        });
      }
      markers.forEach(({ dot, glow, beam }) => {
        const facing = dot.position.clone().normalize().dot(camN) > 0.05;
        const o = vis && facing ? 1 : 0;
        dot.material.opacity += (o - dot.material.opacity) * 0.06;
        glow.material.opacity += (o * 0.4 - glow.material.opacity) * 0.06;
        beam.material.opacity += (o * 0.3 - beam.material.opacity) * 0.06;
        if (vis && facing)
          glow.scale.setScalar(1 + Math.sin(t * 3 + dot.position.x * 10) * 0.4);
      });
      // ── Enhanced Shooting Star Animation ──
      ssT++;
      // Spawn rate: more frequent, 1-3 on screen at a time
      if (ssT > 40 + Math.random() * 80) {
        spawnSS();
        ssT = 0;
      }
      // Animate meteors
      for (let i = shootingStars.length - 1; i >= 0; i--) {
        const s = shootingStars[i];
        s.life++;
        // Apply gravity (slight downward curve)
        s.vel.y -= s.gravity;
        // Shift trail positions: each point takes the previous point's position
        for (let j = s.trail - 1; j > 0; j--) {
          s.pos[j * 3]     = s.pos[(j - 1) * 3];
          s.pos[j * 3 + 1] = s.pos[(j - 1) * 3 + 1];
          s.pos[j * 3 + 2] = s.pos[(j - 1) * 3 + 2];
        }
        // Move head
        s.start.add(s.vel);
        s.pos[0] = s.start.x;
        s.pos[1] = s.start.y;
        s.pos[2] = s.start.z;
        s.pts.geometry.attributes.position.needsUpdate = true;

        // Update vertex colors — fade the trail dynamically as life progresses
        const lifeRatio = s.life / s.max;
        const globalFade = lifeRatio < 0.1 ? lifeRatio / 0.1 : (lifeRatio > 0.7 ? 1.0 - (lifeRatio - 0.7) / 0.3 : 1.0);
        for (let j = 0; j < s.trail; j++) {
          const trailFade = Math.pow(1.0 - j / s.trail, 1.5);
          const brightness = trailFade * globalFade;
          const c = j < 3 ? s.palette.head : s.palette.trail;
          s.colors[j * 3]     = c[0] * brightness;
          s.colors[j * 3 + 1] = c[1] * brightness;
          s.colors[j * 3 + 2] = c[2] * brightness;
        }
        s.pts.geometry.attributes.color.needsUpdate = true;

        // Track head glow to the front of the trail
        if (s.headGlow) {
          s.headGlow.position.set(s.pos[0], s.pos[1], s.pos[2]);
          s.headGlow.material.opacity = globalFade * 0.9;
          s.headGlow.scale.setScalar(0.8 + Math.sin(s.life * 0.5) * 0.3); // flicker
        }

        // Death
        if (s.life >= s.max) {
          // Fireball fragmentation burst
          if (s.isBright && Math.random() < 0.7) {
            fragments.push(spawnFragments(s.start, s.palette));
          }
          ssGroup.remove(s.pts);
          s.pts.geometry.dispose();
          s.pts.material.dispose();
          if (s.headGlow) {
            ssGroup.remove(s.headGlow);
            s.headGlow.geometry.dispose();
            s.headGlow.material.dispose();
          }
          shootingStars.splice(i, 1);
        }
      }
      // Animate fragment bursts
      for (let i = fragments.length - 1; i >= 0; i--) {
        const f = fragments[i];
        f.life++;
        const fade = 1.0 - f.life / f.max;
        for (let j = 0; j < f.vels.length; j++) {
          f.fragPos[j * 3]     += f.vels[j].x;
          f.fragPos[j * 3 + 1] += f.vels[j].y;
          f.fragPos[j * 3 + 2] += f.vels[j].z;
          f.vels[j].y -= 0.0003; // gravity on fragments
        }
        f.fragPts.geometry.attributes.position.needsUpdate = true;
        f.fragPts.material.opacity = fade * 0.8;
        if (f.life >= f.max) {
          ssGroup.remove(f.fragPts);
          f.fragPts.geometry.dispose();
          f.fragPts.material.dispose();
          fragments.splice(i, 1);
        }
      }
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const w2 = container.clientWidth,
        h2 = container.clientHeight;
      camera.aspect = w2 / h2;
      camera.updateProjectionMatrix();
      renderer.setSize(w2, h2);
    };
    window.addEventListener("resize", onResize);
    sceneRef.current = {
      renderer,
      layers: activeLayer,
      showVisibility,
      cleanup: () => {
        cancelAnimationFrame(sceneRef.current.frameId);
        window.removeEventListener("resize", onResize);
        renderer.dispose();
        if (container.contains(el)) container.removeChild(el);
      },
    };
    setLoadProgress(100);
    setLoadStatus("Ready!");
    setTimeout(() => setLoading(false), 400);
  }, []);

  useEffect(() => {
    initScene();
    return () => sceneRef.current.cleanup?.();
  }, [initScene]);
  useEffect(() => {
    if (sceneRef.current) sceneRef.current.layers = activeLayer;
  }, [activeLayer]);
  useEffect(() => {
    if (sceneRef.current) sceneRef.current.showVisibility = showVisibility;
  }, [showVisibility]);
  useEffect(() => {
    if (sceneRef.current) sceneRef.current.visLocs = visibilityLocations;
  }, [visibilityLocations]);

  // ═══════════════════════════════════════════════════════════════
  //  UI
  // ═══════════════════════════════════════════════════════════════
  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        background: "#000",
        fontFamily: lang === "ar" ? "'Noto Sans Arabic','JetBrains Mono',sans-serif" : "'JetBrains Mono','SF Mono','Fira Code',monospace",
        color: "#e0e8f0",
        position: "relative",
        overflow: "hidden",
        userSelect: "none",
        touchAction: "none",
        direction: "ltr",
        paddingTop: "env(safe-area-inset-top)",
      }}
    >
      {loading && <LoadingScreen progress={loadProgress} status={loadStatus} />}
      <div
        ref={mountRef}
        style={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
        }}
      />

      {/* HEADER */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          padding: isMobile ? "20px 14px" : "20px 24px",
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.7) 0%, transparent 100%)",
          zIndex: 10,
          pointerEvents: "none",
          direction: "ltr",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, direction: "ltr", flexWrap: "wrap" }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: apiConnected ? "#00ff88" : "#ffaa00",
              boxShadow: `0 0 12px ${apiConnected ? "#00ff88" : "#ffaa00"}`,
              animation: "pulse 2s infinite",
            }}
          />
          <span
            style={{
              fontSize: 11,
              letterSpacing: 4,
              textTransform: "uppercase",
              color: "#6688aa",
            }}
          >
            {apiConnected ? t.liveData : t.offline}
          </span>
          {moonData && !isMobile && (
            <span style={{ fontSize: 10, color: "#445566", marginLeft: 8 }}>
              🌙 {moonData.phase_name} ({moonData.illumination_pct}%)
            </span>
          )}
        </div>
        <h1
          style={{
            margin: "4px 0 0",
            fontSize: isMobile ? 14 : 22,
            fontWeight: 700,
            letterSpacing: 1,
            background: "linear-gradient(135deg, #88ccff, #00ff88)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            direction: "ltr",
            textAlign: "left",
            maxWidth: isMobile ? "55%" : "none",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {t.title}
        </h1>
        <p
          style={{
            margin: "4px 0 0",
            fontSize: 10,
            color: "#445566",
            letterSpacing: 1.5,
            display: isMobile ? "none" : "block",
            maxWidth: 350,
            direction: "ltr",
            textAlign: "left",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {t.subtitle}
          {apiConnected && lastUpdate && (
            <span style={{ marginLeft: 12, color: "#334455" }}>
              {t.updated} {lastUpdate}
            </span>
          )}
        </p>
      </div>

      {/* LAYER CONTROLS */}
      <div
        style={{
          position: "absolute",
          top: isMobile ? 90 : 120,
          left: isMobile ? 8 : 20,
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          gap: 6,
          direction: "ltr",
        }}
      >
        <button
          onClick={() => setShowVisibility(!showVisibility)}
          style={{
            padding: isMobile ? "8px 10px" : "12px 16px",
            background: showVisibility
              ? "rgba(0,255,136,0.12)"
              : "rgba(255,255,255,0.04)",
            border: `1px solid ${showVisibility ? "#00ff8844" : "rgba(255,255,255,0.08)"}`,
            borderRadius: 8,
            color: showVisibility ? "#00ff88" : "#8899aa",
            cursor: "pointer",
            fontSize: isMobile ? 9 : 11,
            letterSpacing: 1,
            textTransform: "uppercase",
            display: "flex",
            alignItems: "center",
            gap: 8,
            backdropFilter: "blur(12px)",
            transition: "all 0.3s",
          }}
        >
          <span style={{ fontSize: 16 }}>☄</span>
          {showVisibility ? t.hideVis : t.showVis}
        </button>
        {[
          {
            key: "satellites",
            label: t.satellites,
            color: "#00ccff",
            count: satCount,
          },
          {
            key: "debris",
            label: t.spaceDebris,
            color: "#ff6644",
            count: 400,
          },
          {
            key: "asteroids",
            label: t.neoAsteroids,
            color: "#cc8844",
            count: neoCount,
          },
        ].map(({ key, label, color, count }) => (
          <button
            key={key}
            onClick={() => setActiveLayer((p) => ({ ...p, [key]: !p[key] }))}
            style={{
              padding: isMobile ? "7px 10px" : "10px 14px",
              background: activeLayer[key]
                ? `${color}11`
                : "rgba(255,255,255,0.02)",
              border: `1px solid ${activeLayer[key] ? color + "33" : "rgba(255,255,255,0.05)"}`,
              borderRadius: 8,
              color: activeLayer[key] ? color : "#556677",
              cursor: "pointer",
              fontSize: 10,
              letterSpacing: 1,
              textTransform: "uppercase",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              backdropFilter: "blur(12px)",
              transition: "all 0.3s",
              minWidth: isMobile ? 140 : 180,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: activeLayer[key] ? color : "#334",
                  boxShadow: activeLayer[key] ? `0 0 8px ${color}` : "none",
                  transition: "all 0.3s",
                }}
              />
              {label}
            </div>
            <span style={{ opacity: 0.4, fontSize: 9 }}>{count}</span>
          </button>
        ))}
      </div>

      {/* METEOR SHOWER SCHEDULE (Live) */}
      <div
        style={{
          position: "absolute",
          bottom: isMobile ? 10 : 20,
          left: isMobile ? 10 : 20,
          zIndex: 10,
          background: "rgba(0,4,12,0.7)",
          backdropFilter: "blur(16px)",
          border: "1px solid rgba(255,255,255,0.05)",
          borderRadius: 12,
          padding: isMobile ? "10px 12px" : "16px 20px",
          maxWidth: isMobile ? 220 : 320,
          display: isMobile ? "none" : "block",
          direction: "ltr",
        }}
      >
        <div
          style={{
            fontSize: 9,
            letterSpacing: 3,
            color: "#4466aa",
            marginBottom: 10,
            textTransform: "uppercase",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{t.meteorShowers}</span>
          {apiConnected && (
            <span style={{ color: "#00ff88", fontSize: 8, letterSpacing: 1 }}>
              ● {t.live}
            </span>
          )}
        </div>
        {meteorShowers.slice(0, 4).map((s, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "7px 0",
              borderBottom: i < 3 ? "1px solid rgba(255,255,255,0.03)" : "none",
            }}
          >
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#c0d0e0" }}>
                {s.name}
              </div>
              <div style={{ fontSize: 9, color: "#445566" }}>
                {s.active_start} – {s.active_end}
              </div>
              {s.parent_body && (
                <div style={{ fontSize: 8, color: "#334455" }}>
                  {s.parent_body}
                </div>
              )}
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, color: "#00ff88", fontWeight: 600 }}>
                ~{s.zhr}/hr
              </div>
              <div style={{ fontSize: 9, color: "#445566" }}>
                {t.peak} {s.peak_start} – {s.peak_end}
              </div>
              {s.speed_kms && (
                <div style={{ fontSize: 8, color: "#334455" }}>
                  {s.speed_kms} km/s
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* LOCATION DETAIL PANEL (Live Scores) */}
      {selectedLocation &&
        (() => {
          // Look up latest live data for this city
          const live =
            visibilityLocations.find((v) => v.city === selectedLocation.city) ||
            selectedLocation;
          const score = live.overall_score ?? live.score ?? 50;
          return (
            <div
              style={{
                position: "absolute",
                top: isMobile ? "auto" : "50%",
                bottom: isMobile ? 0 : "auto",
                right: isMobile ? 0 : 20,
                left: isMobile ? 0 : "auto",
                transform: isMobile ? "none" : "translateY(-50%)",
                zIndex: 20,
                background: "rgba(0,4,12,0.88)",
                backdropFilter: "blur(24px)",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: isMobile ? "16px 16px 0 0" : 16,
                padding: isMobile ? 18 : 24,
                width: isMobile ? "100%" : 300,
                boxSizing: "border-box",
                maxHeight: isMobile ? "60vh" : "none",
                overflowY: isMobile ? "auto" : "visible",
                animation: isMobile ? "slideUp 0.3s ease" : "slideIn 0.3s ease",
                boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
                direction: "ltr",
              }}
            >
              <button
                onClick={() => setSelectedLocation(null)}
                style={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  background: "rgba(255,255,255,0.05)",
                  border: "none",
                  color: "#556677",
                  cursor: "pointer",
                  fontSize: 14,
                  padding: "4px 8px",
                  borderRadius: 6,
                }}
              >
                ✕
              </button>
              <div
                style={{
                  fontSize: 9,
                  letterSpacing: 3,
                  color: "#4466aa",
                  marginBottom: 4,
                  textTransform: "uppercase",
                }}
              >
                {t.visReport}{" "}
                {apiConnected && (
                  <span style={{ color: "#00ff88" }}>● {t.live}</span>
                )}
              </div>
              <h2
                style={{
                  margin: "0 0 2px",
                  fontSize: 20,
                  fontWeight: 700,
                  color: "#e0f0ff",
                }}
              >
                {live.city}
              </h2>
              <p style={{ margin: "0 0 16px", fontSize: 11, color: "#556677" }}>
                {live.country}
              </p>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 16,
                  padding: 14,
                  background: "rgba(255,255,255,0.02)",
                  borderRadius: 10,
                  border: `1px solid ${getScoreColor(score)}15`,
                }}
              >
                <div
                  style={{
                    width: 54,
                    height: 54,
                    borderRadius: "50%",
                    border: `3px solid ${getScoreColor(score)}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    fontWeight: 700,
                    color: getScoreColor(score),
                    boxShadow: `0 0 24px ${getScoreColor(score)}33`,
                  }}
                >
                  {score}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: getScoreColor(score),
                    }}
                  >
                    {score >= 80 ? t.excellent : score >= 60 ? t.good : score >= 40 ? t.fair : t.poor}
                  </div>
                  <div style={{ fontSize: 10, color: "#556677" }}>
                    {t.visReport}
                  </div>
                </div>
              </div>
              {[
                {
                  l: t.bestTime,
                  v: `${live.best_time_start || "12:00 AM"} – ${live.best_time_end || "4:00 AM"}`,
                  i: "🕐",
                },
                {
                  l: t.weather,
                  v: live.condition_main || live.weather || "—",
                  i: "🌤️",
                },
                {
                  l: t.moonPhase,
                  v: live.moon_phase
                    ? `${live.moon_phase} (${live.moon_illumination}%)`
                    : moonData
                      ? `${moonData.phase_name} (${moonData.illumination_pct}%)`
                      : "—",
                  i: "🌙",
                },
                {
                  l: t.lightPollution,
                  v: live.bortle_class
                    ? `Bortle ${live.bortle_class} – ${getBortleLabel(live.bortle_class)}`
                    : "—",
                  i: "💡",
                },
              ].map(({ l, v, i: ic }, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 0",
                    borderBottom: "1px solid rgba(255,255,255,0.025)",
                  }}
                >
                  <span
                    style={{
                      fontSize: 10,
                      color: "#556677",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span style={{ fontSize: 12 }}>{ic}</span> {l}
                  </span>
                  <span
                    style={{
                      fontSize: isMobile ? 10 : 11,
                      color: "#c0d0e0",
                      fontWeight: 500,
                      textAlign: "right",
                      maxWidth: isMobile ? "50%" : 160,
                      wordBreak: "break-word",
                    }}
                  >
                    {v}
                  </span>
                </div>
              ))}
              {live.weather_score !== undefined &&
                live.weather_score !== null && (
                  <div
                    style={{
                      marginTop: 12,
                      paddingTop: 12,
                      borderTop: "1px solid rgba(255,255,255,0.04)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 9,
                        letterSpacing: 2,
                        color: "#4466aa",
                        marginBottom: 8,
                        textTransform: "uppercase",
                      }}
                    >
                      {t.scoreBreakdown}
                    </div>
                    {[
                      {
                        label: t.weatherPct,
                        val: live.weather_score,
                        w: "40%",
                        color: "#00ccff",
                      },
                      {
                        label: t.moonPct,
                        val: live.moon_score,
                        w: "25%",
                        color: "#ffcc00",
                      },
                      {
                        label: t.lightPct,
                        val: live.light_pollution_score,
                        w: "20%",
                        color: "#ff8844",
                      },
                      {
                        label: t.showerPct,
                        val: live.shower_activity_score,
                        w: "15%",
                        color: "#cc66ff",
                      },
                    ].map(({ label, val, w, color }, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 4,
                        }}
                      >
                        <span
                          style={{ fontSize: 9, color: "#556677", width: 85 }}
                        >
                          {label} ({w})
                        </span>
                        <div
                          style={{
                            flex: 1,
                            height: 3,
                            background: "rgba(255,255,255,0.05)",
                            borderRadius: 2,
                          }}
                        >
                          <div
                            style={{
                              width: `${val ?? 0}%`,
                              height: "100%",
                              background: color,
                              borderRadius: 2,
                              transition: "width 0.5s ease",
                            }}
                          />
                        </div>
                        <span
                          style={{
                            fontSize: 9,
                            color: color,
                            width: 24,
                            textAlign: "right",
                          }}
                        >
                          {val ?? "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              {live.note && (
                <div
                  style={{
                    fontSize: 9,
                    color: "#334455",
                    marginTop: 8,
                    fontStyle: "italic",
                  }}
                >
                  {live.note}
                </div>
              )}
            </div>
          );
        })()}

      {/* FOOTER */}
      <div
        style={{
          position: "absolute",
          bottom: 20,
          right: 20,
          zIndex: 10,
          fontSize: 9,
          color: "#223344",
          letterSpacing: 0.8,
          textAlign: "right",
          lineHeight: 2,
          display: isMobile ? "none" : "block",
          direction: "ltr",
        }}
      >
        {t.dragRotate}
        {showVisibility && (
          <>
            <br />
            {t.clickMarkers}
          </>
        )}
        <br />
        <span style={{ color: "#1a2a3a" }}>
          {t.earthCredits}
        </span>
        {apiConnected && (
          <>
            <br />
            <span style={{ color: "#1a3a2a" }}>
              {t.dataCredits}
            </span>
          </>
        )}
      </div>

      {showAnalytics && (
        <AnalyticsPanel onClose={() => setShowAnalytics(false)} lang={lang} />
      )}

      {/* TOP-RIGHT BUTTONS */}
      <div style={{ position: "absolute", top: isMobile ? 16 : 20, right: isMobile ? 10 : 20, zIndex: 50, display: "flex", gap: 6, direction: "ltr", pointerEvents: "auto" }}>
        {/* Language Toggle */}
        <button
          onClick={() => setLang((l) => (l === "en" ? "ar" : "en"))}
          style={{
            height: isMobile ? 32 : 40,
            padding: isMobile ? "0 10px" : "0 14px",
            borderRadius: 20,
            background: "rgba(6,14,28,0.9)",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(255,255,255,0.15)",
            color: "#c0d0e0",
            cursor: "pointer",
            fontSize: isMobile ? 10 : 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 4,
            boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
            transition: "all 0.3s",
            fontFamily: "'JetBrains Mono','SF Mono',monospace",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; }}
        >
          🌐 {lang === "en" ? "عربي" : "EN"}
        </button>
        {/* Analytics */}
        <button
          onClick={() => setShowAnalytics(true)}
          style={{
            height: isMobile ? 32 : 40,
            padding: isMobile ? "0 10px" : "0 16px",
            borderRadius: 20,
            background: "rgba(6,14,28,0.9)",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(68,221,255,0.25)",
            color: "#44ddff",
            cursor: "pointer",
            fontSize: isMobile ? 10 : 13,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            boxShadow: "0 4px 20px rgba(0,0,0,0.5), 0 0 15px rgba(68,221,255,0.1)",
            transition: "all 0.3s",
            fontFamily: "'JetBrains Mono','SF Mono',monospace",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(68,221,255,0.5)"; e.currentTarget.style.boxShadow = "0 4px 24px rgba(0,0,0,0.5), 0 0 20px rgba(68,221,255,0.25)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(68,221,255,0.25)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.5), 0 0 15px rgba(68,221,255,0.1)"; }}
        >
          📊 <span style={{ fontSize: isMobile ? 8 : 10, letterSpacing: 2 }}>{t.analytics}</span>
        </button>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;600;700&display=swap');
        @keyframes slideIn { from { opacity:0; transform:translateY(-50%) translateX(20px); } to { opacity:1; transform:translateY(-50%) translateX(0); } }
        @keyframes slideUp { from { opacity:0; transform:translateY(100%); } to { opacity:1; transform:translateY(0); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
        @supports(padding: max(0px)) {
          body { padding-top: env(safe-area-inset-top) !important; }
        }
      `}</style>
    </div>
  );
}
